// /src/pages/users/UserEdit.tsx
//
// Admin user edit page.
//
// Design goals:
// - Keep a compact, single-column layout (maxWidth=900).
// - Use AppPageHeader slots for the primary CTA (Save) and user context (leading title).
// - Persist no page-level header UI after unmount (must clear slots).
//
// Save rules:
// - Enabled only when the form is dirty AND valid AND not saving.
// - When Save is triggered but blocked, show a helpful System Alert.
//
// Danger Zone:
// - Password Reset: sends reset email (guarded by global Confirm).
// - Delete User: permanently deletes the user (guarded by global Confirm).
//
// Notes:
// - Username + Name are read-only (identity).
// - Email validates format client-side and may fail server-side for duplication.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ContentCopy, DeleteOutline, LockReset, Save as SaveIcon } from '@mui/icons-material';

import { ADMIN_USER_GROUPS, UserGroup, UserStatus, type User } from '@models/user';
import { deleteUser, getUser, recoverPassword, updateUser } from '@apis/users';
import { copyToClipboard } from '@utils/common';
import { USER_GROUP_META } from '@constants/userMeta';

import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';
import DangerZoneCard from '@components/common/DangerZoneCard';

type Option<T> = { label: string; value: T; description?: string };
type FieldErrorKey = 'group' | 'position' | 'email' | 'phoneNumber' | 'status';
type FieldErrorMap = Partial<Record<FieldErrorKey, string>>;

const OK_CODE = 2000;

const normalize = (v?: string | null) => (v ?? '').trim();
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const parseApiError = (rawMessage: string) => {
  const msg = rawMessage || 'Request failed.';
  const lower = msg.toLowerCase();

  // MySQL duplicate example (keep this mapping in sync with backend errors if they change).
  if (msg.includes('Duplicate entry') && msg.includes('user.user_idx_email')) {
    return {
      userMessage: 'Email is already in use. Please choose another one.',
      fieldErrors: { email: 'This email is already in use.' } as FieldErrorMap,
    };
  }

  // Bean validation examples.
  if (lower.includes('should not be blank')) {
    if (lower.includes('phonenumber')) {
      return {
        userMessage: 'Phone number is required.',
        fieldErrors: { phoneNumber: 'Phone number is required.' } as FieldErrorMap,
      };
    }
    if (lower.includes('email')) {
      return {
        userMessage: 'Email is required.',
        fieldErrors: { email: 'Email is required.' } as FieldErrorMap,
      };
    }
    if (lower.includes('position')) {
      return {
        userMessage: 'Position is required.',
        fieldErrors: { position: 'Position is required.' } as FieldErrorMap,
      };
    }
  }

  return { userMessage: msg, fieldErrors: {} as FieldErrorMap };
};

const UserEdit = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setPageHeaderSlots } = usePageHeader();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const { userId } = useParams();
  const safeUserId = useMemo(() => Number(userId), [userId]);

  const groupOptions = useMemo<Option<UserGroup>[]>(
    () =>
      ADMIN_USER_GROUPS.map((g) => ({
        value: g,
        label: USER_GROUP_META[g]?.label ?? String(g),
        description: USER_GROUP_META[g]?.description ?? '',
      })),
    [],
  );

  const canQuery = Number.isFinite(safeUserId) && safeUserId > 0;

  const { data: userRes, isLoading, isError, refetch } = useQuery({
    queryKey: ['user', safeUserId],
    queryFn: () => getUser(safeUserId),
    enabled: canQuery,
    refetchOnMount: 'always',
  });

  const loaded: User | undefined = userRes?.data?.[0];

  // Editable fields only (identity is read-only).
  const [form, setForm] = useState<User>({
    userId: 0,
    username: '',
    name: '',
    group: UserGroup.MANAGER,
    status: UserStatus.ACTIVE,
    position: '',
    email: '',
    phoneNumber: '',
  });

  // Baseline snapshot for dirty detection.
  const [origin, setOrigin] = useState(() => ({
    group: UserGroup.MANAGER as UserGroup,
    position: '',
    email: '',
    phoneNumber: '',
    status: UserStatus.ACTIVE as UserStatus,
  }));

  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [saving, setSaving] = useState(false);

  // Shared busy state for danger actions (reset / delete).
  const [dangerBusy, setDangerBusy] = useState(false);

  useEffect(() => {
    if (!loaded) return;

    setForm(loaded);
    setOrigin({
      group: loaded.group,
      position: loaded.position ?? '',
      email: loaded.email ?? '',
      phoneNumber: loaded.phoneNumber ?? '',
      status: loaded.status,
    });
    setFieldErrors({});
  }, [loaded]);

  const emailValue = useMemo(() => normalize(form.email), [form.email]);
  const emailFormatInvalid = Boolean(emailValue) && !isValidEmail(emailValue);

  const isDirty = useMemo(() => {
    return (
      form.group !== origin.group ||
      normalize(form.position) !== normalize(origin.position) ||
      normalize(form.email) !== normalize(origin.email) ||
      normalize(form.phoneNumber) !== normalize(origin.phoneNumber) ||
      form.status !== origin.status
    );
  }, [form, origin]);

  const isFormValid = useMemo(() => {
    const position = normalize(form.position);
    const email = normalize(form.email);
    const phone = normalize(form.phoneNumber);

    if (!form.group) return false;
    if (!position) return false;
    if (!email) return false;
    if (!isValidEmail(email)) return false;
    if (!phone) return false;

    return true;
  }, [form]);

  const canSave = isDirty && isFormValid && !saving;

  const groupValue = useMemo<Option<UserGroup>>(
    () => groupOptions.find((o) => o.value === form.group) ?? groupOptions[0],
    [groupOptions, form.group],
  );

  const groupHelper = useMemo(() => USER_GROUP_META[form.group]?.description ?? '', [form.group]);

  const invalidateUsersCaches = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['users'] });
    await queryClient.invalidateQueries({ queryKey: ['usersCount'] });
  }, [queryClient]);

  const handleCopyId = useCallback(() => {
    const id = normalize(form.username);
    if (!id) return;

    copyToClipboard(id);
    systemAlert.success('ID copied.');
  }, [form.username, systemAlert]);

  const handleSave = useCallback(async () => {
    if (!canSave) {
      systemAlert.info(isDirty ? 'Please fill in all required fields correctly.' : 'No changes to save.');
      return;
    }

    setSaving(true);
    setFieldErrors({});

    try {
      const payload = {
        userId: form.userId,
        position: normalize(form.position),
        group: form.group,
        email: normalize(form.email),
        phoneNumber: normalize(form.phoneNumber),
        status: form.status,
      };

      const res = await updateUser(form.userId, payload);

      if (res?.code !== OK_CODE) {
        const parsed = parseApiError(String(res?.message ?? 'Failed to update user.'));
        setFieldErrors(parsed.fieldErrors);
        systemAlert.error(parsed.userMessage);
        return;
      }

      setOrigin({
        group: form.group,
        position: form.position ?? '',
        email: form.email ?? '',
        phoneNumber: form.phoneNumber ?? '',
        status: form.status,
      });

      systemAlert.success('User updated successfully.');

      await invalidateUsersCaches();
      await refetch();
    } catch (e: any) {
      const parsed = parseApiError(String(e?.message ?? 'Failed to update user.'));
      setFieldErrors(parsed.fieldErrors);
      systemAlert.error(parsed.userMessage);
    } finally {
      setSaving(false);
    }
  }, [canSave, form, invalidateUsersCaches, isDirty, refetch, systemAlert]);

  const handlePwReset = useCallback(async () => {
    if (!loaded) return;

    const ok = await confirm({
      title: 'Send password reset email?',
      description: 'This will send a password reset email to the user. Proceed carefully.',
      confirmText: 'Send reset email',
      cancelText: 'Cancel',
      priority: 'warning',
      autoFocusConfirm: false,
    });
    if (!ok) return;

    setDangerBusy(true);
    try {
      const res = await recoverPassword({ username: loaded.username, email: loaded.email });

      const success = res?.code === OK_CODE || String(res?.message ?? '').toUpperCase() === 'OK';
      if (!success) {
        const parsed = parseApiError(String(res?.message ?? 'Failed to reset password.'));
        systemAlert.error(parsed.userMessage);
        return;
      }

      systemAlert.success('Password reset email has been sent.');
    } catch (e: any) {
      systemAlert.error(String(e?.message ?? 'Failed to reset password.'));
    } finally {
      setDangerBusy(false);
    }
  }, [confirm, loaded, systemAlert]);

  const handleDelete = useCallback(async () => {
    if (!loaded) return;

    const ok = await confirm({
      title: 'Delete this user?',
      description: 'Permanently removes the user. This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      priority: 'danger',
      autoFocusConfirm: false,
    });
    if (!ok) return;

    setDangerBusy(true);
    try {
      await deleteUser(loaded.userId);

      systemAlert.success('User deleted.');
      await invalidateUsersCaches();
      navigate('/users');
    } catch (e: any) {
      systemAlert.error(String(e?.message ?? 'Failed to delete user.'));
    } finally {
      setDangerBusy(false);
    }
  }, [confirm, invalidateUsersCaches, loaded, navigate, systemAlert]);

  /**
   * AppPageHeader slots:
   * - Leading: user display name (context).
   * - Actions: Save (primary CTA).
   * Always clear on unmount to avoid leaking actions into other routes.
   */
  useEffect(() => {
    setPageHeaderSlots({
      leading: loaded?.name ? { title: loaded.name } : undefined,
      actions: [
        {
          key: 'save-user',
          label: saving ? 'Saving…' : 'Save',
          startIcon: <SaveIcon />,
          variant: 'contained',
          primary: true,
          disabled: !canSave,
          onClick: handleSave,
        },
      ],
    });

    return () => setPageHeaderSlots({});
  }, [setPageHeaderSlots, loaded?.name, saving, canSave, handleSave]);

  if (!canQuery) {
    return (
      <Box p={2}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography color="error" fontWeight={800}>
            Invalid user ID.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2, maxWidth: 900, width: '100%', mr: 'auto' }}>
      {isError ? (
        <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <Typography color="error" fontWeight={800} sx={{ flex: 1 }}>
              Failed to load user.
            </Typography>
            <Button onClick={() => refetch()} variant="outlined">
              Retry
            </Button>
          </Stack>
        </Paper>
      ) : null}

      <Stack spacing={3} sx={{ minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            <Typography variant="subtitle1" fontWeight={900}>
              Identity
            </Typography>

            {isLoading ? (
              <Stack spacing={1.25}>
                <Skeleton height={56} />
                <Skeleton height={56} />
              </Stack>
            ) : (
              <>
                <TextField
                  label="ID"
                  size="small"
                  value={form.username}
                  fullWidth
                  disabled
                  InputProps={{
                    endAdornment: normalize(form.username) ? (
                      <Tooltip title="Copy">
                        <IconButton size="small" onClick={handleCopyId} aria-label="Copy ID" edge="end">
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : undefined,
                  }}
                />
                <TextField label="Name" size="small" value={form.name} fullWidth disabled />
              </>
            )}

            <Divider />

            <Typography variant="subtitle1" fontWeight={900}>
              Access & Contact
            </Typography>

            {isLoading ? (
              <Skeleton height={56} />
            ) : (
              <Autocomplete
                options={groupOptions}
                value={groupValue}
                disableClearable
                onChange={(_e, opt) => {
                  setForm((prev) => ({ ...prev, group: opt.value }));
                  setFieldErrors((prev) => ({ ...prev, group: undefined }));
                }}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Group"
                    size="small"
                    error={Boolean(fieldErrors.group)}
                    helperText={fieldErrors.group ?? groupHelper}
                  />
                )}
                renderOption={(props, opt) => (
                  <li {...props} key={String(opt.value)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" fontWeight={700}>
                        {opt.label}
                      </Typography>
                      {opt.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {opt.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </li>
                )}
              />
            )}

            {isLoading ? (
              <Skeleton height={56} />
            ) : (
              <TextField
                label="Position"
                size="small"
                value={form.position ?? ''}
                fullWidth
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, position: e.target.value }));
                  setFieldErrors((prev) => ({ ...prev, position: undefined }));
                }}
                error={Boolean(fieldErrors.position)}
                helperText={fieldErrors.position}
              />
            )}

            {isLoading ? (
              <Skeleton height={56} />
            ) : (
              <TextField
                label="E-mail"
                size="small"
                value={form.email ?? ''}
                fullWidth
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, email: e.target.value }));
                  setFieldErrors((prev) => ({ ...prev, email: undefined }));
                }}
                error={Boolean(fieldErrors.email) || emailFormatInvalid}
                helperText={
                  fieldErrors.email ??
                  (!emailValue ? undefined : emailFormatInvalid ? 'Invalid email format.' : undefined)
                }
              />
            )}

            {isLoading ? (
              <Skeleton height={56} />
            ) : (
              <TextField
                label="Phone"
                size="small"
                value={form.phoneNumber ?? ''}
                fullWidth
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, phoneNumber: e.target.value }));
                  setFieldErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                }}
                error={Boolean(fieldErrors.phoneNumber)}
                helperText={fieldErrors.phoneNumber}
              />
            )}

            <Divider />

            <Typography variant="subtitle1" fontWeight={900}>
              Status
            </Typography>

            <Paper
              variant="outlined"
              sx={(theme) => {
                const active = form.status === UserStatus.ACTIVE;
                const border = active ? theme.palette.success.main : theme.palette.grey[400];
                const bg =
                  theme.palette.mode === 'light'
                    ? alpha(active ? theme.palette.success.main : theme.palette.grey[600], 0.08)
                    : 'transparent';

                return {
                  borderRadius: 2,
                  borderColor: fieldErrors.status ? theme.palette.error.main : border,
                  backgroundColor: fieldErrors.status ? alpha(theme.palette.error.main, 0.06) : bg,
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                };
              }}
            >
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Chip
                    size="small"
                    label={form.status === UserStatus.ACTIVE ? 'ACTIVE' : 'INACTIVE'}
                    color={form.status === UserStatus.ACTIVE ? 'success' : 'default'}
                    variant={form.status === UserStatus.ACTIVE ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 900, letterSpacing: 0.4 }}
                  />
                  <Typography variant="subtitle2" fontWeight={900} noWrap>
                    Status
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {form.status === UserStatus.ACTIVE
                    ? 'User can sign in and use admin features.'
                    : 'User is disabled and cannot sign in.'}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <Typography variant="body2" fontWeight={800}>
                  {form.status === UserStatus.ACTIVE ? 'Active' : 'Inactive'}
                </Typography>

                <Switch
                  checked={form.status === UserStatus.ACTIVE}
                  onChange={(_e, checked) => {
                    setForm((prev) => ({
                      ...prev,
                      status: checked ? UserStatus.ACTIVE : UserStatus.INACTIVE,
                    }));
                    setFieldErrors((prev) => ({ ...prev, status: undefined }));
                  }}
                />
              </Stack>
            </Paper>

            {fieldErrors.status ? (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
                {fieldErrors.status}
              </Typography>
            ) : null}
          </Stack>
        </Paper>

        <DangerZoneCard
          priority="warning"
          title="Password Reset"
          description="Sends a password reset email to the user."
          actions={
            <Button
              variant="contained"
              color="warning"
              startIcon={<LockReset />}
              onClick={handlePwReset}
              disabled={isLoading || !loaded || dangerBusy}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {dangerBusy ? 'Sending…' : 'Send reset email'}
            </Button>
          }
        />

        <DangerZoneCard
          priority="danger"
          title="Delete User"
          description="Permanently removes the user. This action cannot be undone."
          actions={
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={handleDelete}
              disabled={isLoading || !loaded || dangerBusy}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {dangerBusy ? 'Deleting…' : 'Delete'}
            </Button>
          }
        />
      </Stack>
    </Box>
  );
};

export default UserEdit;
